import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { verifyOrgCode } from '../auth/authApi';
import Layout from '../components/Layout';
import Spinner from '../components/Spinner';
import { safetyTest, shuffle, SAFETY_VIDEO_URL } from '../data/safetyTest';

const TERMS_VERSION = 'v1';

const inputStyle = {
  width: '100%', padding: '12px', borderRadius: '6px',
  border: '1px solid rgba(255, 255, 255, 0.3)', backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#fff', fontSize: '1em', outline: 'none',
};
const labelStyle = { display: 'block', marginBottom: '8px', color: '#fff', fontWeight: '500' };
const fieldWrap = { marginBottom: '20px' };
const orange = '#FF5722';
const errBox = {
  padding: '10px', marginBottom: '15px',
  backgroundColor: 'rgba(255, 87, 34, 0.2)', border: '1px solid #FF5722',
  borderRadius: '6px', color: '#FF5722', fontSize: '0.9em',
};
const primaryBtn = (disabled) => ({
  width: '100%', padding: '15px', borderRadius: '6px', border: 'none',
  backgroundColor: disabled ? '#999' : orange, color: '#fff',
  fontSize: '1.1em', fontWeight: 'bold', cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background-color 0.3s ease',
});
const checkRow = {
  display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px',
  color: '#fff', fontSize: '0.95em', lineHeight: 1.5, cursor: 'pointer',
};
const checkbox = { marginTop: '3px', width: '18px', height: '18px', accentColor: orange, flex: 'none', cursor: 'pointer' };

const isAtLeast18 = (dobValue) => {
  if (!dobValue) return false;
  const dob = new Date(`${dobValue}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return false;
  const eighteenth = new Date(dob);
  eighteenth.setFullYear(dob.getFullYear() + 18);
  return eighteenth <= new Date();
};
const maxDobFor18 = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
})();

const Register = () => {
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgCodeError, setOrgCodeError] = useState('');
  const [dobError, setDobError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 2 — terms + safety video
  const [videoWatched, setVideoWatched] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  // Step 3 — safety test
  const quiz = useMemo(() => safetyTest.questions.map((q) => ({ ...q, answers: shuffle(q.answers) })), []);
  const [answers, setAnswers] = useState({}); // { [questionId]: answerText }
  const [acks, setAcks] = useState(() => safetyTest.acknowledgments.map(() => false));

  const navigate = useNavigate();
  const { user, register } = useAuth();

  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  useEffect(() => { if (user) navigate('/portal'); }, [user, navigate]);

  const validateDob = (value) => {
    if (!value) { setDobError('Date of birth is required.'); return false; }
    if (!isAtLeast18(value)) { setDobError('You must be at least 18 years old to create an account.'); return false; }
    setDobError('');
    return true;
  };

  const checkOrgCode = async () => {
    const code = orgCode.trim();
    setOrgName(''); setOrgCodeError('');
    if (!code) {
      setOrgCodeError('Organization code is required.');
      return false;
    }
    const result = await verifyOrgCode(code);
    if (result.valid) {
      setOrgName(result.organizationName);
      return true;
    }
    setOrgCodeError('That organization code isn’t valid.');
    return false;
  };

  // Step 1 → 2
  const goToTerms = async () => {
    setError('');
    if (!fullName || !email || !password || !dateOfBirth || !orgCode.trim()) {
      setError('Please fill in your name, email, password, date of birth, and organization code.');
      if (!dateOfBirth) setDobError('Date of birth is required.');
      if (!orgCode.trim()) setOrgCodeError('Organization code is required.');
      return;
    }
    if (!validateDob(dateOfBirth)) { setError('You must be at least 18 years old to create an account.'); return; }
    const orgOk = await checkOrgCode();
    if (!orgOk) { setError('Please enter a valid organization code.'); return; }
    setStep(2);
  };

  // Step 2 → 3
  const goToTest = () => {
    setError('');
    if (!videoWatched || !termsAgreed) {
      setError('Please watch the safety video and agree to the Terms of Service to continue.');
      return;
    }
    setStep(3);
  };

  const allCorrect = () => quiz.every((q) => {
    const correct = q.answers.find((a) => a.isCorrect);
    return correct && answers[q.id] === correct.text;
  });
  const allAcked = () => acks.every(Boolean);
  const allAnswered = () => quiz.every((q) => answers[q.id] != null);

  const handleFinalSubmit = async () => {
    setError('');
    if (!allAnswered()) { setError('Please answer every safety question.'); return; }
    if (!allCorrect()) { setError('One or more answers are incorrect. Every safety question must be answered correctly — please review and try again.'); return; }
    if (!allAcked()) { setError('Please check all safety acknowledgments.'); return; }

    setLoading(true);
    try {
      await register({
        fullName, email, password, dateOfBirth,
        termsVersion: TERMS_VERSION,
        organizationCode: orgCode.trim(),
        completedSafetyTest: true,
      });
      navigate('/portal');
    } catch (err) {
      if (err.message === 'email_taken') setError('An account with this email already exists. Try logging in.');
      else if (err.message === 'invalid_organization_code') setError('That organization code isn’t valid.');
      else setError('Could not create your account. Please try again.');
      setStep(1); // send them back to fix account details
    } finally {
      setLoading(false);
    }
  };

  const StepDots = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '22px' }}>
      {[1, 2, 3].map((n) => (
        <span key={n} style={{
          width: n === step ? '26px' : '9px', height: '9px', borderRadius: '20px',
          backgroundColor: n <= step ? orange : 'rgba(255,255,255,0.25)', transition: 'all .25s',
        }} />
      ))}
    </div>
  );

  return (
    <Layout>
      <section id="wrapper">
        <div className="inner" style={{ paddingTop: '40px' }}>
          <h2 className="major" style={{ textAlign: 'center' }}>
            {step === 1 ? 'Create your account' : step === 2 ? 'Safety & terms' : 'Safety test'}
          </h2>

          <div className="content" style={{
            maxWidth: '800px', margin: '20px auto 40px', padding: '30px',
            backgroundColor: 'rgba(27, 31, 34, 0.85)', borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          }}>
            <StepDots />
            {error && <div style={errBox}>{error}</div>}

            {/* STEP 1 — Account */}
            {step === 1 && (
              <>
                <div style={fieldWrap}>
                  <label htmlFor="fullName" style={labelStyle}>Full name</label>
                  <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" style={inputStyle} />
                </div>
                <div style={fieldWrap}>
                  <label htmlFor="email" style={labelStyle}>Email</label>
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" style={inputStyle} />
                </div>
                <div style={fieldWrap}>
                  <label htmlFor="password" style={labelStyle}>Password</label>
                  <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password" style={inputStyle} />
                </div>
                <div style={fieldWrap}>
                  <label htmlFor="dob" style={labelStyle}>Date of birth</label>
                  <input id="dob" type="date" value={dateOfBirth}
                    onChange={(e) => { setDateOfBirth(e.target.value); if (e.target.value) validateDob(e.target.value); else setDobError(''); }}
                    onBlur={() => validateDob(dateOfBirth)} max={maxDobFor18} required
                    style={{ ...inputStyle, borderColor: dobError ? orange : 'rgba(255,255,255,0.3)' }} />
                  {dobError
                    ? <div style={{ marginTop: '6px', color: orange, fontSize: '0.85em' }}>{dobError}</div>
                    : <div style={{ marginTop: '6px', color: 'rgba(255,255,255,0.65)', fontSize: '0.85em' }}>You must be 18 or older to register.</div>}
                </div>
                <div style={fieldWrap}>
                  <label htmlFor="orgCode" style={labelStyle}>Organization code</label>
                  <input id="orgCode" type="text" value={orgCode}
                    onChange={(e) => { setOrgCode(e.target.value); setOrgName(''); setOrgCodeError(''); }}
                    onBlur={checkOrgCode} required placeholder="Enter your organization code"
                    style={{ ...inputStyle, borderColor: orgCodeError ? orange : 'rgba(255,255,255,0.3)' }} />
                  {orgName && <div style={{ marginTop: '8px', color: '#8bc34a', fontSize: '0.9em' }}>You&rsquo;re joining <strong>{orgName}</strong></div>}
                  {orgCodeError && <div style={{ marginTop: '8px', color: orange, fontSize: '0.9em' }}>{orgCodeError}</div>}
                </div>
                <button type="button" onClick={goToTerms} style={primaryBtn(false)}>Continue</button>
              </>
            )}

            {/* STEP 2 — Terms + safety video */}
            {step === 2 && (
              <>
                <p style={{ color: '#fff', opacity: 0.85, marginBottom: '16px', lineHeight: 1.5 }}>
                  Before you train, please watch the safety video and review our terms.
                </p>
                <video controls playsInline preload="metadata" src={SAFETY_VIDEO_URL}
                  style={{ width: '100%', borderRadius: '8px', backgroundColor: '#000', marginBottom: '20px' }} />

                <label style={checkRow}>
                  <input type="checkbox" style={checkbox} checked={videoWatched} onChange={(e) => setVideoWatched(e.target.checked)} />
                  <span>I have watched the <strong>Range Drills Safety Video</strong> and agree to follow its safety guidelines.</span>
                </label>
                <label style={checkRow}>
                  <input type="checkbox" style={checkbox} checked={termsAgreed} onChange={(e) => setTermsAgreed(e.target.checked)} />
                  <span>I have read and agree to the{' '}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: orange, textDecoration: 'none' }}>Terms of Service</a>.
                  </span>
                </label>

                <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                  <button type="button" onClick={() => setStep(1)} style={{ ...primaryBtn(false), backgroundColor: 'rgba(255,255,255,0.12)', flex: '0 0 40%' }}>Back</button>
                  <button type="button" onClick={goToTest} disabled={!videoWatched || !termsAgreed} style={{ ...primaryBtn(!videoWatched || !termsAgreed), flex: 1 }}>Continue</button>
                </div>
              </>
            )}

            {/* STEP 3 — Safety test */}
            {step === 3 && (
              <>
                <p style={{ color: '#fff', opacity: 0.85, marginBottom: '18px', lineHeight: 1.5 }}>
                  Answer every question correctly to finish. This confirms you understand safe firearm handling.
                </p>

                {quiz.map((q, qi) => (
                  <div key={q.id} style={{ marginBottom: '22px' }}>
                    <div style={{ color: '#fff', fontWeight: 600, marginBottom: '10px' }}>{qi + 1}. {q.question}</div>
                    {q.answers.map((a, ai) => (
                      <label key={ai} style={{ ...checkRow, marginBottom: '8px' }}>
                        <input type="radio" name={`q${q.id}`} style={{ ...checkbox, borderRadius: '50%' }}
                          checked={answers[q.id] === a.text}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: a.text }))} />
                        <span>{a.text}</span>
                      </label>
                    ))}
                  </div>
                ))}

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '18px', marginBottom: '4px' }}>
                  <div style={{ color: '#fff', fontWeight: 600, marginBottom: '12px' }}>Acknowledgments</div>
                  {safetyTest.acknowledgments.map((ack, i) => (
                    <label key={i} style={checkRow}>
                      <input type="checkbox" style={checkbox} checked={acks[i]}
                        onChange={(e) => setAcks((prev) => prev.map((v, idx) => (idx === i ? e.target.checked : v)))} />
                      <span>{ack}</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '18px' }}>
                  <button type="button" onClick={() => setStep(2)} disabled={loading} style={{ ...primaryBtn(false), backgroundColor: 'rgba(255,255,255,0.12)', flex: '0 0 40%' }}>Back</button>
                  <button type="button" onClick={handleFinalSubmit} disabled={loading} style={{ ...primaryBtn(loading), flex: 1 }}>
                    {loading ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={16} />Creating account…</span>) : 'Create account'}
                  </button>
                </div>
              </>
            )}

            <p style={{ marginTop: '22px', textAlign: 'center', fontSize: '0.9em', opacity: '0.8', color: '#fff' }}>
              Already have an account? <Link to="/login" style={{ color: orange, textDecoration: 'none' }}>Log in</Link>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Register;
